import { FC, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FullFrameDiagnosticPhotosGalleryContainer } from '@higo/common/lib/components';
import { useSkinExaminationResultsQuery } from '@higo/common/lib/features//ExaminationPage/hooks/queries';
import { ExaminationPageSection } from '@higo/common/lib/features/ExaminationPage/sections/ExaminationPageSection';
import { ExaminationPageSectionId } from '@higo/common/lib/features/ExaminationPage/models';
import { VisualExaminationDiagnosticViewerModel } from '@higo/common/lib/features/ExaminationPage/components/VisualExaminationDiagnosticViewer/VisualExaminationDiagnosticViewerProps';
import {
  useSkinExaminationResultsLinks,
  useSkinExaminationVideoFramesIds,
  useSkinExaminationVideoResultsLink,
} from '@higo/common/lib/features/ExaminationPage/sections/SkinExaminationSection/hooks';
import { VisualExaminationDiagnosticViewerForPublic } from '@higo/common/lib/features/ExaminationPage/components/VisualExaminationDiagnosticViewer';
import { WithTogglingViewer } from '@higo/common/lib/features/ExaminationPage/components/VisualExaminationDiagnosticViewer/WithTogglingViewer';
import {
  DataLoadingSpinner,
  CenteredDataErrorInfo,
  CenteredCanceledInfo,
} from '@higo/common/lib/components/LoadingAndErrorHandlingItems';
import { useVideoProcessQuery } from '@higo/doctor/src/hooks/queries/useVideoProcessQuery';

export interface SkinExaminationSectionProps {
  examinationId: number;
  requestGenerateSkinExaminationVideoButtonSlot?: ReactNode;
  hasTogglingGalleryVideo?: boolean;
  handleSkinImageForPDFChange?: (index: string) => void;
  handleIsSkinDataLoadingChange?: (index: boolean) => void;
  handleIsSkinImageDiagnosticChange?: (index: boolean) => void;
  isStatusCanceledByUser?: boolean;
  isVisitSection?: boolean;
}

const model: VisualExaminationDiagnosticViewerModel = {
  usePhysicalExaminationResultsQuery: useSkinExaminationResultsQuery,
  usePhysicalExaminationGalleryResultsLinks: useSkinExaminationResultsLinks,
  usePhysicalExaminationVideoFramesIds: useSkinExaminationVideoFramesIds,
  usePhysicalExaminationVideoResultsLink: useSkinExaminationVideoResultsLink,
};

const galleryViewerProps = {
  thumbnailsSlidesPerView: 8,
};

export const SkinExaminationSectionForVisits: FC<SkinExaminationSectionProps> =
  ({
    examinationId,
    requestGenerateSkinExaminationVideoButtonSlot,
    hasTogglingGalleryVideo,
    handleSkinImageForPDFChange,
    handleIsSkinImageDiagnosticChange,
    handleIsSkinDataLoadingChange,
    isStatusCanceledByUser,
    isVisitSection,
  }) => {
    const {
      data,
      isFetched: isDataFetched,
      isLoading: isDataLoading,
    } = useSkinExaminationResultsQuery(examinationId);
    const {
      data: galleryResults,
      isFetched: isGalleryFetched,
      isLoading: isGallerryLoading,
    } = model.usePhysicalExaminationGalleryResultsLinks(examinationId);
    const rowData = data?.skinResults;

    const [skinGalleryActiveLink, setskinGalleryActiveLink] = useState('');

    const handleskinGalleryActiveLinkChange = (index: string) => {
      setskinGalleryActiveLink(index);
    };

    const diagnosticIndex = data && data.diagnosticImageIndex;

    const skinDiagnosticImageLink =
      data?.diagnosticImageIndex &&
      galleryResults &&
      galleryResults.find(
        (item) =>
          diagnosticIndex &&
          item.fileName === data.skinResults[diagnosticIndex].filename,
      )?.link;

    const skinMiddleImageLink =
      data?.skinResults &&
      galleryResults &&
      galleryResults[Math.floor(data?.skinResults.length / 2)]?.link;

    const skinImageForPDF =
      skinDiagnosticImageLink || skinGalleryActiveLink || skinMiddleImageLink;
    data &&
      skinImageForPDF &&
      handleSkinImageForPDFChange &&
      handleSkinImageForPDFChange(skinImageForPDF);
    data &&
      skinImageForPDF &&
      handleIsSkinImageDiagnosticChange &&
      handleIsSkinImageDiagnosticChange(data.hasDiagnosticImage);
    handleIsSkinDataLoadingChange &&
      handleIsSkinDataLoadingChange(!isDataFetched || !isGalleryFetched);

    useVideoProcessQuery(examinationId);

    return (
      <ExaminationPageSection
        title={
          <FormattedMessage id="examinationPage.section.skinExamination.title" />
        }
        sectionId={ExaminationPageSectionId.Skin}
        isStatusCanceledByUser={isStatusCanceledByUser}
        isVisitSection={isVisitSection}
      >
        {isStatusCanceledByUser ? (
          <CenteredCanceledInfo />
        ) : (
          !isGalleryFetched && !isGallerryLoading && !isDataLoading && (
            <CenteredDataErrorInfo />
          )
        )}
        {isGallerryLoading && <DataLoadingSpinner />}
        {isDataFetched && isGalleryFetched && (
          <FullFrameDiagnosticPhotosGalleryContainer>
            {hasTogglingGalleryVideo ? (
              <WithTogglingViewer
                examinationId={examinationId}
                model={model}
                requestGenerateVideoButtonSlot={
                  requestGenerateSkinExaminationVideoButtonSlot
                }
                galleryViewerProps={galleryViewerProps}
                rowData={rowData}
                setActiveGalleryLink={handleskinGalleryActiveLinkChange}
                isStatusCanceledByUser={isStatusCanceledByUser}
              />
            ) : (
              <VisualExaminationDiagnosticViewerForPublic
                examinationId={examinationId}
                model={model}
                galleryViewerProps={galleryViewerProps}
                rowData={rowData}
                isStatusCanceledByUser={isStatusCanceledByUser}
              />
            )}
          </FullFrameDiagnosticPhotosGalleryContainer>
        )}
      </ExaminationPageSection>
    );
  };
